import React, { useCallback } from 'react';
import { Component } from '@/types';
import { KanbanBoard } from '@/components/pm/KanbanBoard';
import { useComponentDataResolver } from '@/services/component-data/useComponentDataResolver';
import { useInteractionContext } from './InteractionContextRuntime';
import type { ComponentDataResolverResult } from '@/services/component-data/componentDataResolver';

export interface SharedKanbanBoardComponentProps {
  component: Component;
  isViewerMode?: boolean;
  inheritedDataResolver?: ComponentDataResolverResult;
}

interface SharedKanbanBoardResolvedProps {
  component: Component;
  isViewerMode: boolean;
  dataResolver: ComponentDataResolverResult;
}

const SharedKanbanBoardResolved: React.FC<SharedKanbanBoardResolvedProps> = ({
  component,
  isViewerMode,
  dataResolver,
}) => {
  const interaction = useInteractionContext();

  const handleAddItem = useCallback(() => {
    interaction.handlers.onAddKanbanItem?.(component.id);
  }, [interaction, component.id]);

  const handleEditItem = useCallback((itemId: string, itemData: Record<string, unknown>) => {
    interaction.handlers.onEditKanbanItem?.(component.id, itemId, itemData);
  }, [interaction, component.id]);

  // Loading state
  if (dataResolver.loading && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="kanban-loading"
        className="w-full h-full flex items-center justify-center gap-2 text-muted-foreground text-sm"
      >
        <svg
          className="animate-spin"
          style={{ width: 20, height: 20 }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        Loading board data...
      </div>
    );
  }

  // Error state
  if (dataResolver.error && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="kanban-error"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span className="text-sm text-destructive">{dataResolver.error.message}</span>
        <button
          onClick={() => dataResolver.refetch()}
          style={{
            fontSize: 12,
            color: '#3b82f6',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <KanbanBoard
      component={component}
      data={dataResolver.rows}
      isViewerMode={isViewerMode}
      writeTargetPermissions={dataResolver.writeTarget.operations}
      writeTargetRequestingOrganizationId={
        dataResolver.writeTarget.requestingOrganizationId
      }
      onAddItem={handleAddItem}
      onEditItem={handleEditItem}
    />
  );
};

const SharedKanbanBoardStandalone: React.FC<
  Pick<SharedKanbanBoardComponentProps, 'component' | 'isViewerMode'>
> = ({ component, isViewerMode = false }) => {
  const dataResolver = useComponentDataResolver(component);
  return (
    <SharedKanbanBoardResolved
      component={component}
      isViewerMode={isViewerMode}
      dataResolver={dataResolver}
    />
  );
};

export const SharedKanbanBoardComponent: React.FC<SharedKanbanBoardComponentProps> = ({
  component,
  isViewerMode = false,
  inheritedDataResolver,
}) => {
  if (inheritedDataResolver !== undefined) {
    return (
      <SharedKanbanBoardResolved
        component={component}
        isViewerMode={isViewerMode}
        dataResolver={inheritedDataResolver}
      />
    );
  }

  return (
    <SharedKanbanBoardStandalone
      component={component}
      isViewerMode={isViewerMode}
    />
  );
};

SharedKanbanBoardComponent.displayName = 'SharedKanbanBoardComponent';
